'use client';

import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useRef, useState } from 'react';

import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { REORDER_KEY, reorderIntoCart, type ReorderResult } from './reorder-action';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export type { ReorderResult } from './reorder-action';

/** Shared pending state prevents two cards (or a double click) from repeating concurrently. */
export function useReorder(order: Pick<CustomerOrder, 'id'> | null | undefined) {
  const client = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken ?? '');
  const pendingCount = useIsMutating({ mutationKey: REORDER_KEY });
  const running = useRef(false);
  const [outcome, setOutcome] = useState<{
    authToken: string;
    locale: string;
    orderId: number;
    result: ReorderResult;
  } | null>(null);
  const mutation = useMutation({
    mutationKey: REORDER_KEY,
    retry: false,
    // Offline clicks must fail visibly, never become delayed duplicate writes on reconnect.
    networkMode: 'always',
    mutationFn: (orderId: number) =>
      reorderIntoCart(
        client,
        { orderId, authToken, locale },
        () =>
          useCustomerStore.getState().me?.authToken === authToken &&
          useLocaleStore.getState().currentLocale === locale,
      ),
  });

  const reorder = async () => {
    if (!order?.id || running.current || client.isMutating({ mutationKey: REORDER_KEY })) return;
    if (!authToken) {
      router.push(ROUTES.SIGN_IN(search ? `${pathname}?${search}` : pathname));
      return;
    }
    running.current = true;
    setOutcome(null);
    try {
      const result = await mutation.mutateAsync(order.id);
      if (
        useCustomerStore.getState().me?.authToken !== authToken ||
        useLocaleStore.getState().currentLocale !== locale
      )
        return;
      setOutcome({ authToken, locale, orderId: order.id, result });
      if (result.status === 401) {
        router.push(ROUTES.SIGN_IN(search ? `${pathname}?${search}` : pathname));
      }
      // A refreshed list may remove the card, so keep the unavailable-order message visible.
      if (result.status === 404)
        toast({ description: result.messages.join(' '), variant: 'destructive' });
    } finally {
      running.current = false;
    }
  };

  return {
    available: !!authToken && !!order?.id,
    reorder,
    pending: mutation.isPending,
    disabled: pendingCount > 0,
    result:
      outcome?.authToken === authToken && outcome.locale === locale && outcome.orderId === order?.id
        ? outcome.result
        : null,
  };
}
